/**
 * Подсказки пользователей для полей ввода (приглашение в комнату, новый DM).
 *
 * ИСТОЧНИК ДАННЫХ — ТОЛЬКО ЛОКАЛЬНЫЕ. Антон 2026-09-12 08:51: подсказки
 * строятся по людям, УЖЕ находящимся в том же пространстве, что и комната.
 * Серверный searchUserDirectory НЕЛЬЗЯ использовать: он отдаёт всех подряд,
 * включая людей вне пространства — это прямое нарушение постановки.
 *
 * Почему отдельный модуль, а не логика в компоненте: правила «кого показывать»
 * нетривиальны (определение пространства, фильтры по членству, исключение себя
 * и уже приглашённых) и должны жить рядом с Matrix-слоем, а не в UI.
 */
import type { MatrixClient } from "matrix-js-sdk";

import { tf } from "../i18n";
import { spaceMembership } from "./rooms";

/** Одна строка подсказки: кого можно выбрать. */
export interface UserSuggestion {
  userId: string;
  /** Экранное имя (display name). Может совпадать у разных людей. */
  displayName: string;
  /** Готовый http(s) URL аватара или null. */
  avatarUrl: string | null;
}


/**
 * Собрать уникальных участников перечисленных комнат.
 * Вынесено отдельно, потому что нужно в двух режимах: «люди пространства»
 * и «все, кого знаю» (комната вне пространств).
 */
function collectMembers(
  client: MatrixClient,
  roomIds: string[],
  me: string,
  excludeRoomId?: string,
): UserSuggestion[] {
  const seen = new Map<string, UserSuggestion>();
  for (const rId of roomIds) {
    const room = client.getRoom(rId);
    if (!room) continue;
    for (const m of room.getMembers()) {
      if (m.membership !== "join" && m.membership !== "invite") continue;
      if (m.userId === me) continue;
      if (seen.has(m.userId)) continue;
      seen.set(m.userId, {
        userId: m.userId,
        displayName: m.name || m.userId,
        avatarUrl: m.getMxcAvatarUrl() ?? null,
      });
    }
  }
  if (excludeRoomId) {
    const target = client.getRoom(excludeRoomId);
    if (target) {
      for (const m of target.getMembers()) {
        if (m.membership === "join" || m.membership === "invite") seen.delete(m.userId);
      }
    }
  }
  return [...seen.values()].sort((a, b) =>
    a.displayName.localeCompare(b.displayName, "ru"),
  );
}

/**
 * Собрать подсказки пользователей для поля ввода в контексте комнаты.
 *
 * Алгоритм:
 *  1. Определяем, в каких пространствах состоит комната (spaceMembership
 *     из rooms.ts — переиспользуем готовый обход с защитой от циклов).
 *  2. Берём ВСЕ комнаты этих пространств, собираем участников со статусом
 *     join/invite — это «люди этого пространства».
 *  3. Склеиваем в один список без повторов по MXID.
 *  4. Убираем себя (пригласить самого себя нельзя).
 *  5. Если задан excludeRoomId — убираем тех, кто уже в этой комнате
 *     (join ИЛИ invite). Решение Антона 12.09 08:53: «я бы не показывал
 *     тех кто уже в комнате». Leave и ban НЕ исключаем: вышедшего можно
 *     позвать обратно, а забаненному сервер вернёт внятную ошибку.
 *
 * Комната ВНЕ пространств → список пуст, поле работает как раньше
 * (ввод полного MXID руками). Это не ошибка, а следствие постановки:
 * подсказки только по людям пространства.
 *
 * Кэшировать не нужно: вызов идёт по локальным данным при открытии диалога,
 * а не на каждое нажатие клавиши.
 */
export function spaceUserSuggestions(
  client: MatrixClient,
  roomId: string | null,
  excludeRoomId?: string,
  /**
   * АКТИВНОЕ ПРОСТРАНСТВО из рельсы слева (Антон 12.09 10:52):
   * «если у меня активирован home — я вижу вообще всех при создании; если
   * активировано пространство ALG — вижу всех членов ALG. Если я
   * пользователь, я вижу только членов того пространства, которое выбрано».
   *
   * null/undefined = «Дом»: подсказки по ВСЕМ моим пространствам.
   * Конкретный id = сужаем до одного пространства.
   *
   * Это фильтр ОБЛАСТИ ВИДИМОСТИ, а не прав: показать можно только тех,
   * кто и так доступен по правилу границы. Сужение — удобство админа,
   * у которого пространств много, и он не хочет видеть всех сразу.
   */
  activeSpaceId?: string | null,
): UserSuggestion[] {
  const me = client.getUserId();
  if (!me) return [];

  // Шаг 1: в каких пространствах состоит комната.
  // roomId=null — диалог DM, у него нет целевой комнаты; подсказки собираем
  // по ВСЕМ пространствам, где состоит хоть одна комната (см. ниже).
  const memberOf = spaceMembership(client);
  const spaceIdsAll =
    roomId !== null
      ? (memberOf.get(roomId) ?? new Set<string>())
      : // Для DM целевой комнаты нет: берём ВСЕ пространства, где я состою.
        //
        // Раньше здесь стоял обход memberOf.values() — то есть пространства
        // выводились из ДОЧЕРНИХ комнат. Пространство без комнат (или где я
        // ещё не вошёл ни в одну) в этот список не попадало вовсе, и
        // «Новый личный чат» показывал пустой дропдаун (дефект 12.09 10:46).
        // Берём напрямую из клиента — источник тот же, что у spaceMembership.
        new Set(
          client
            .getRooms()
            .filter((r) => r.isSpaceRoom() && r.getMyMembership() === "join")
            .map((r) => r.roomId),
        );

  // Сужение до активного пространства. Применяем ТОЛЬКО если это
  // пространство и правда среди доступных: иначе выбранная вкладка,
  // не относящаяся к комнате, обнулила бы подсказки без объяснения.
  const spaceIds =
    activeSpaceId && spaceIdsAll.has(activeSpaceId)
      ? new Set([activeSpaceId])
      : spaceIdsAll;

  // КОМНАТА ВНЕ ПРОСТРАНСТВ — ПОДСКАЗЫВАЕМ ВСЕХ, КОГО ЗНАЕМ.
  //
  // Антон 12.09 10:49: «если комната создана вне любого пространства —
  // согласен, ты можешь пригласить всех участников по твоему Matrix».
  // Правило границы здесь не действует, а значит и подсказки не должны
  // молчать: до этого исправления диалог в такой комнате писал
  // «подсказывать некого» (скриншот 10:52, комната novaja) — формально
  // верно, практически бесполезно.
  //
  // «Всех, кого знаю» = участники всех моих комнат: другого источника у
  // клиента нет, а серверный каталог пользователей запрещён постановкой
  // (он отдаёт людей и вне моей видимости).
  if (spaceIds.size === 0) {
    return collectMembers(
      client,
      client.getRooms().filter((r) => !r.isSpaceRoom()).map((r) => r.roomId),
      me,
      excludeRoomId,
    );
  }

  // Шаг 2: откуда брать людей.
  //
  // ГЛАВНОЕ ИСПРАВЛЕНИЕ (Антон 12.09 10:46: «в новый личный чат тоже
  // дропдаун не работает»): САМО ПРОСТРАНСТВО — тоже комната, и его
  // участники это и есть «члены спейса». Раньше сюда попадали только
  // ДОЧЕРНИЕ комнаты (spaceMembership строит карту «комната → её
  // пространства» и сами пространства в неё не кладёт). Из-за этого
  // человек, добавленный в пространство, но ещё не вошедший ни в одну
  // комнату внутри, не показывался нигде — а по правилу безопасности
  // Антона (10:45) именно членство В ПРОСТРАНСТВЕ даёт доступ.
  //
  // Порядок важен: сначала сами пространства, потом их комнаты.
  const spaceRoomIds = new Set<string>(spaceIds);
  for (const [rId, sIds] of memberOf) {
    for (const sId of sIds) {
      if (spaceIds.has(sId)) spaceRoomIds.add(rId);
    }
  }

  // Шаг 3-4: собрать участников комнат пространства, убрать себя и тех,
  // кто уже в целевой комнате.
  const seen = new Map<string, UserSuggestion>(
    collectMembers(client, [...spaceRoomIds], me, excludeRoomId).map((u) => [u.userId, u]),
  );

  // Сортировка по экранному имени — список сканируется глазами,
  // случайный порядок заставлял бы читать каждую строку.
  return [...seen.values()].sort((a, b) =>
    a.displayName.localeCompare(b.displayName, "ru"),
  );
}

/**
 * ГРАНИЦА ПРОСТРАНСТВА — ПРЕДИКАТ, А НЕ ОТКАЗ.
 *
 * Антон 12.09 10:45 (голосом): «крайне важный момент безопасности —
 * добавлять в звонок и ещё куда-то можно только персонажей в рамках
 * одного спейса». Это правило реализовано ЧЕРЕЗ ПОДСКАЗКИ: дропдаун
 * показывает только людей пространства, серверный каталог не зовём.
 *
 * Антон 05.10 14:17: РУЧНОЙ ВВОД ПОЛНОГО АДРЕСА РАЗРЕШИТЬ. Раньше здесь
 * был жёсткий отказ: функция возвращала текст ошибки и приглашение
 * блокировалось даже при корректном MXID. На iOS того же дня блокировки
 * уже не было — и Антон подтвердил, что верное поведение именно такое.
 * Теперь функция — чистый предикат «человек вне пространства комнаты»,
 * а РЕШЕНИЕ о том, что с этим фактом делать (предупредить и дать
 * отправить), принимает вызывающий UI.
 *
 * Возвращает true, если человек НЕ состоит в пространстве комнаты.
 * false — состоит, или правило неприменимо (сам себя, комната вне
 * пространств).
 */
export function isOutsideSpace(
  client: MatrixClient,
  roomId: string,
  userId: string,
): boolean {
  const target = userId.trim().toLowerCase();
  if (!target) return false;

  // Считаем по ТОМУ ЖЕ источнику, что и подсказки, но БЕЗ исключения
  // участников комнаты: человек может уже быть в комнате — это не повод
  // говорить «он вне пространства».
  const inSpace = spaceUserSuggestions(client, roomId);
  if (inSpace.some((u) => u.userId.toLowerCase() === target)) return false;

  // Сам себя — всегда можно (подсказки себя не показывают).
  if (client.getUserId()?.toLowerCase() === target) return false;

  // Комната вне пространств: правило неприменимо, границы нет.
  const spaces = spaceMembership(client).get(roomId);
  if (!spaces || spaces.size === 0) return false;

  return true;
}

/**
 * Тот же барьер, но для ЛИЧНОГО ЧАТА, где целевой комнаты ещё нет.
 *
 * Антон 12.09 10:47: «если у тебя человек, а нет пространства, ты не
 * можешь с ним ни создать чат, ни пригласить его куда-то. Общаемся
 * только в рамках одного пространства».
 *
 * Условие проще, чем у комнаты: достаточно, чтобы человек состоял хотя бы
 * в ОДНОМ пространстве, где состою я. Какое именно — неважно: общая
 * принадлежность уже означает, что кто-то с правами его туда впустил.
 *
 * ВАЖНО ПРО АДМИНОВ (Антон 10:47: «единственный, кто может ходить сквозь
 * пространства, это админ; на сегодня админов два — ты и я»). Отдельной
 * проверки «я админ» здесь НЕТ, и это сознательно: у админа общие
 * пространства с кем угодно и так есть, поэтому правило его не стесняет.
 * Захардкоживать список MXID в клиентский код нельзя — это была бы
 * защита, снимаемая правкой в браузере. Настоящее разграничение прав
 * живёт на сервере (power levels), клиент лишь не предлагает недопустимое.
 */
export function whyDmNotAllowed(
  client: MatrixClient,
  userId: string,
): string | null {
  const target = userId.trim().toLowerCase();
  if (!target) return null;
  if (client.getUserId()?.toLowerCase() === target) return null;

  const allowed = spaceUserSuggestions(client, null);
  if (allowed.some((u) => u.userId.toLowerCase() === target)) return null;

  // Пространств нет вообще: правило неприменимо — иначе клиент без
  // пространств не мог бы начать ни одного чата. Это не дыра: без
  // пространств и границы нет.
  const mySpaces = client
    .getRooms()
    .filter((r) => r.isSpaceRoom() && r.getMyMembership() === "join");
  if (mySpaces.length === 0) return null;

  return tf("space.notInAnySpace", { user: userId });
}
